// QRScanner.jsx
import React, { useState, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Divider,
  Button
} from '@mui/material';
import { QrCodeScanner, UploadFile } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import jsQR from 'jsqr';

const QRScanner = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const processQRData = (data) => {
    try {
      setError('');
      let visitId = '';

      // Case 1: JSON structured QR code (internal)
      if (data.startsWith('{')) {
        const parsed = JSON.parse(data);

        if (parsed.type !== 'visitor_checkout') {
          setError('Invalid QR code type. Please scan a valid checkout QR.');
          return;
        }

        if (parsed.visitorUserID && user?.uid !== parsed.visitorUserID) {
          setError('This QR code is not for your visit. Please scan your own checkout QR.');
          return;
        }

        const params = new URLSearchParams(parsed).toString();
        navigate(`/visitor-checkout?${params}`);
        setSuccess(true);
        return;
      }

      // Case 2: URL with visitId (external scan QR)
      if (data.includes('visitId=')) {
        const url = new URL(data);
        visitId = url.searchParams.get('visitId');
        if (!visitId) throw new Error();
        navigate(`/visitor-checkout?visitId=${visitId}`);
        setSuccess(true);
        return;
      }

      // Case 3: Direct visitId only (fallback)
      if (/^[a-zA-Z0-9]{20,}$/.test(data)) {
        visitId = data;
        navigate(`/visitor-checkout?visitId=${visitId}`);
        setSuccess(true);
        return;
      }

      // If none of the above match
      throw new Error();
    } catch {
      setError('Invalid QR code format. Please try again.');
    }
  };

  const handleFileChange = async (event) => {
    setLoading(true);
    setError('');
    setSuccess(false);
    const file = event.target.files[0];
    if (!file) return;

    try {
      const img = new Image();
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      img.onload = () => {
        const canvas = canvasRef.current;
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, img.width, img.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data) processQRData(code.data);
        else setError('QR code could not be read. Try another image.');
        setLoading(false);
        URL.revokeObjectURL(url);
      };

      img.onerror = () => {
        setError('Image load error.');
        setLoading(false);
        URL.revokeObjectURL(url);
      };

      img.src = url;
    } catch {
      setError('An error occurred while processing the image.');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#fefce8",
        py: 5,
        px: 2,
      }}
    >
      <Paper
        sx={{
          maxWidth: 700,
          mx: "auto",
          p: 5,
          borderRadius: "20px",
          background: "#ffffff",
          border: "1px solid #f0d060",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)"
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              width: 90,
              height: 90,
              borderRadius: "50%",
              background: "#fffbea",
              border: "2px solid #f0d060",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2
            }}
          >
            <QrCodeScanner
              sx={{
                fontSize: 48,
                color: "#1c1400"
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: "2rem",
              fontWeight: 900,
              fontFamily: "'Unbounded', sans-serif",
              color: "#1c1400",
              mb: 1
            }}
          >
            QR Scanner
          </Typography>

          <Typography
            sx={{
              color: "#8a7a40"
            }}
          >
            Upload your visitor checkout QR code to continue
          </Typography>
        </Box>

        {/* Upload Area */}
        <Box
          sx={{
            border: "2px dashed #f0d060",
            borderRadius: "16px",
            p: 4,
            background: "#fffbea",
            textAlign: "center",
            transition: "0.3s",
            "&:hover": {
              background: "#fff7cc"
            }
          }}
        >
          <Button
            component="label"
            disabled={loading}
            sx={{
              background: "#1c1400",
              color: "#fde047",
              px: 4,
              py: 1.5,
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",

              "&:hover": {
                background: "#f59e0b",
                color: "#1c1400"
              }
            }}
            startIcon={<UploadFile />}
          >
            {loading ? "Scanning..." : "Upload QR Image"}

            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
          </Button>

          <Typography
            sx={{
              mt: 2,
              color: "#8a7a40",
              fontSize: "0.9rem"
            }}
          >
            Upload a QR screenshot or camera photo
          </Typography>
        </Box>

        {/* Preview */}
        {previewUrl && (
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#1c1400",
                mb: 2
              }}
            >
              QR Preview
            </Typography>

            <Box
              component="img"
              src={previewUrl}
              alt="QR Preview"
              sx={{
                maxWidth: "100%",
                maxHeight: 300,
                borderRadius: "12px",
                border: "2px solid #f0d060",
                background: "#fff"
              }}
            />
          </Box>
        )}

        <canvas
          ref={canvasRef}
          style={{ display: "none" }}
        />

        {/* Alerts */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mt: 3,
              borderRadius: "10px"
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{
              mt: 3,
              borderRadius: "10px"
            }}
          >
            QR Code scanned successfully. Redirecting...
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default QRScanner;
